import React from 'react';
import {View} from 'react-native';
import {get} from 'lodash';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import FormattedNumber from '/component/base/FormattedNumber';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import {indent} from '/constant/layout.constant';
import {MetricsSection} from '/constant/TestIDs';
import {LABEL_COMING_SOON, LABEL_SPLIT_COMING_SOON} from '/enum/label.enum';
import {getTextFromReactNode} from '/util/accessibility.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './MetricSection.style';

interface TouchableMetricItemProps {
  item: {
    label?: React.ReactNode;
    value?: number | string;
    truncate?: boolean;
    testID?: string;
    newValue?: number;
    readonly?: boolean;
  };
  index: number;
  totalItemCount: number;
  onPress: () => void;
}

export const TouchableMetricItem: React.FC<TouchableMetricItemProps> = ({
  item = {},
  index = 0,
  totalItemCount = 0,
  onPress = () => {},
}) => {
  const label = get(item, 'label', '');
  const value = get(item, 'value', 0);
  const truncate = get(item, 'truncate', false);
  const testID = get(item, 'testID', '');
  const newValue = get(item, 'newValue', 0);
  const readonly = get(item, 'readonly', false);

  const isValueZero = value === 0 || value === '0';

  const showRightIcon =
    !readonly &&
    !isValueZero &&
    value !== LABEL_SPLIT_COMING_SOON &&
    value !== LABEL_COMING_SOON;

  const labelString = getTextFromReactNode(label);

  return (
    <KeyboardFocusable
      accessible={true}
      accessibilityRole={readonly || isValueZero ? 'none' : 'button'}
      accessibilityLabel={
        Number.isFinite(newValue) && newValue > 0
          ? `${labelString}, ${newValue} new`
          : Number.isFinite(index) && totalItemCount > 0
          ? `${labelString} ${value} : ${index + 1} of ${totalItemCount}`
          : `${labelString} ${value}`
      }
      testID={testID}
      style={styles.listItem}
      onPress={!readonly && !isValueZero ? onPress : undefined}>
      <View accessible={false} style={styles.labelContainer}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          testID={MetricsSection.metricTitle}
          style={[
            styles.listItemLabel,
            styles.listItemLabelResponsive,
            readonly && styles.readonly,
          ]}
          accessible={false}>
          {label}
        </Text>
        {newValue !== 0 && (
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            accessible={false}
            variant="caption"
            style={styles.newLabel}
            {...testProperties(MetricsSection.metricNewCounter)}>
            {`${newValue} New`}
          </Text>
        )}
      </View>
      <View
        accessible={false}
        style={[
          styles.metricContainer,
          readonly && styles.readonly,
          !showRightIcon && {paddingRight: indent * 1.5}, // Compensate for missing icon + its padding
        ]}>
        <FormattedNumber
          testID={MetricsSection.metricValue}
          value={value}
          truncate={truncate}
          accessible={false}
          style={{text: readonly ? styles.readonly : undefined}}
          boldText={true}
          textVariant="body"
        />
        {showRightIcon && (
          <Icon
            name="chevron-right"
            style={styles.arrowIcon}
            accessible={false}
          />
        )}
      </View>
    </KeyboardFocusable>
  );
};

export default TouchableMetricItem;
